/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import moment from 'moment-timezone';

// Chatscope UI imports
import { Message, MessageSeparator } from '@chatscope/chat-ui-kit-react';

// Component imports
import { Avatar, Button, Col, Dropdown, List, Menu, Table, Tooltip, Typography } from 'antd';
import { DownloadOutlined, RobotOutlined, UserOutlined } from '@ant-design/icons';
import { LiveChatIcon } from '../Icons/LiveChatIcon';

// Misc imports
import is from '../../utils/is';
import handleFileDownload from '../../utils/misc/handleFileDownload';
import { useFetchChatSessionInfoQuery, useFetchContactChatSessionsQuery, useLazyDownloadChatSessionQuery } from '../../store/chatSessions/chatSessionsApi';
import { revertBlobTypes } from '../../store/utils/revertBlobTypes';
import { useFetchBotQuery } from '../../store/bots/botsApi';
import { DEFAULT_DATETIME_FORMAT } from '../../constants/main';

// Constant declarations
const { Paragraph, Text } = Typography;
const messageTimeDisplayFormat = 'hh:mm A';

const ContactChatSessionsForm = ({ contact }) => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const tenantId = contact.tenant.id;

  const { data: bot } = useFetchBotQuery({ orgId, tenantId }, { skip: !tenantId });
  const { data: contactChatSessions, isFetching: isFetchingContactChatSessions } = useFetchContactChatSessionsQuery({ orgId, handle: contact.handle, urlParams: { tenantsFilter: [tenantId] } }, { skip: !contact.handle });
  const { currentData: chatsession, isFetching: isFetchingChatSession } = useFetchChatSessionInfoQuery({ orgId, chatSessionId: currentSessionId, urlParams: { tenantsFilter: [tenantId] } }, { skip: !currentSessionId });
  const [downloadChatSession, { isLoading: isDownloadingChatSession }] = useLazyDownloadChatSessionQuery();

  const botTimezone = bot?.timezone ?? 'UTC';

  const columns = useMemo(() => {
    return [
      {
        title: t('Date Created'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: {
          compare: (a, b) => a.createdAt - b.createdAt
        },
        render: createdAt => {
          return (
            moment(createdAt).tz(botTimezone).format(DEFAULT_DATETIME_FORMAT)
          );
        }
      },
      {
        title: t('Last Interaction'),
        dataIndex: 'lastInteraction',
        key: 'lastInteraction',
        responsive: ['xxl'],
        render: lastInteraction => {
          return (
            moment(lastInteraction).tz(botTimezone).format(DEFAULT_DATETIME_FORMAT)
          );
        }
      },
      {
        title: t('Message Count'),
        dataIndex: 'messageCount',
        key: 'messageCount',
        responsive: ['md'],
        sorter: {
          compare: (a, b) => a.messageCount - b.messageCount
        },
        render: messageCount => {
          return `${messageCount} ${t('messages')}`;
        }
      },
      {
        title: t('Duration'),
        dataIndex: 'duration',
        key: 'duration',
        responsive: ['xl'],
        sorter: {
          compare: (a, b) => a.duration - b.duration
        },
        render: duration => {
          return (
            moment.duration(duration, 'seconds').format('hh:mm:ss', { trim: false })
          );
        }
      },
      {
        title: t('Session'),
        dataIndex: 'id',
        key: 'id',
        responsive: ['sm']
      },
      {
        title: t('Actions'),
        key: 'actions',
        fixed: 'right',
        align: 'center',
        render: id => {
          return (
            <Dropdown overlay={menu(id)} trigger={['click']}>
              <Tooltip title={t('Download')}>
                <Button
                  type="text"
                  disabled={isDownloadingChatSession}
                  icon={<DownloadOutlined />}
                  shape="circle"
                />
              </Tooltip>
            </Dropdown>
          );
        }
      }
    ];
  }, []);

  const onSingleSessionDownload = async (fileType, chatSession) => {
    const result = await downloadChatSession({
      orgId: orgId, chatSessionId: chatSession.id, urlParams: {
        fileType: fileType,
        tenantsFilter: [tenantId]
      }
    }).unwrap();

    const file = revertBlobTypes(result);

    const fileName = file?.headers['content-disposition']?.split(new RegExp(['"'], 'g'))?.[1] ?? `${chatSession.id}.${fileType}`;
    handleFileDownload(file?.blob, fileName);
  };

  const handleMenuClick = (e, id) => {
    onSingleSessionDownload(e.key, id);
  };

  const menu = (id) => (
    <Menu
      onClick={(e) => handleMenuClick(e, id)}
      items={[
        {
          label: t('Choose your preferred format'),
          type: 'group'
        },
        {
          label: t('Text'),
          key: 'txt'
        },
        {
          label: t('CSV'),
          key: 'csv'
        }
      ]}
    />
  );

  const formatSubscriberName = (name) => {
    if (!name) {
      return t('Anonymous');
    } else {
      return name[0].toUpperCase() + name.slice(1);
    }
  };

  const onFetchChatSession = async (record) => {
    setCurrentSessionId(record?.id);

    const firstMatch = document.querySelector(`#session-${record.id} .bg-highlight-alt`);
    return firstMatch?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const renderMessage = ((record) =>
    item => ((
      (item.conversationNode === 'handover-accepted'
        ? <MessageSeparator className="mb-5">
          {item.subscriberName}`
        </MessageSeparator> : null)
      || (item.conversationNode === 'handover-ended' ? <MessageSeparator className="mb-5">{t('LiveChat ended')}</MessageSeparator> : null)
      || <li id={item.date} className="mb-10">
        <Avatar
          size={38}
          icon={
            (item.subscriberHandle === record.subscriberHandle ? <UserOutlined /> : null)
            || (item.subscriberName.toLowerCase() === 'agent' ? <RobotOutlined /> : null)
            || <LiveChatIcon />
          }
          className={item.subscriberHandle === record.subscriberHandle ? 'float-left mr-3' : 'float-right ml-3'}
        />
        <span className="chat-bubble">
          <Message
            model={{
              type: 'custom',
              direction: item.subscriberHandle === record.subscriberHandle ? 'incoming' : 'outgoing'
            }}
          >
            <Message.CustomContent>
              <Text className="mr-2" strong>
                {formatSubscriberName(item.subscriberName)}
              </Text>
              <Text type="secondary">
                {item.subscriberEmail ? item.subscriberEmail : null}
              </Text>
              <br />
              <Paragraph
                className="word-break-normal"
              >
                <ReactMarkdown
                  skipHtml
                >
                  {item.message}
                </ReactMarkdown>
              </Paragraph>
            </Message.CustomContent>
          </Message>
          <p className={item.subscriberHandle === record.subscriberHandle ? 'incoming-timestamp' : 'outgoing-timestamp'}>
            {moment(item.date).tz(botTimezone).format(messageTimeDisplayFormat)}
          </p>
        </span>
      </li>
    )));

  const expandChatSession = (record) => {
    return (
      <List
        id={'session-' + record.id}
        dataSource={chatsession?.messages && chatsession?.messages.filter((session) => session.type !== 'TRIGGER')}
        loading={isFetchingChatSession && !chatsession?.messages}
        style={{ maxHeight: 450 }}
        className="overflow-y-auto chat-session-list scrollbar-custom px-3"
        renderItem={renderMessage(record)}
      />
    );
  };

  return (
    <Col xs={28}>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={contactChatSessions && is.array(contactChatSessions.items) && contactChatSessions.items}
        loading={isFetchingContactChatSessions}
        expandable={{
          onExpand: (expanded, record) => {
            const keys = [];
            if (expanded) {
              keys.push(record.id);
              onFetchChatSession(record);
            }
            setExpandedRows(keys);
          },
          expandedRowRender: record => (
            expandChatSession(record)
          ),
          expandedRowKeys: expandedRows
        }}
      >
      </Table>
    </Col>
  );

};

ContactChatSessionsForm.propTypes = {
  contact: PropTypes.object,
  onSuccess: PropTypes.func
};

export default ContactChatSessionsForm;
